import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAdminMe,
  fetchRegistrations,
  fetchLeads,
  fetchAdminStats,
  adminLogout,
} from "@/lib/adminApi";
import {
  Users,
  Inbox,
  MessageSquare,
  LogOut,
  Sparkles,
  RefreshCw,
  Mail,
  Phone,
  Briefcase,
} from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch (_e) {
    return s;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("registrations");
  const [stats, setStats] = useState({ registrations: 0, leads: 0, chat_sessions: 0 });
  const [regs, setRegs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [s, r, l] = await Promise.all([
        fetchAdminStats(),
        fetchRegistrations(),
        fetchLeads(),
      ]);
      setStats(s);
      setRegs(r.items || []);
      setLeads(l.items || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        navigate("/admin/login", { replace: true });
      } else {
        setError("Failed to load data. Try refresh.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const u = await fetchAdminMe();
        setMe(u);
        await loadAll();
      } catch (_e) {
        navigate("/admin/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = async () => {
    await adminLogout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <main className="min-h-screen bg-[#030712] text-white">
      {/* Header */}
      <header className="border-b border-white/8 sticky top-0 backdrop-blur-md z-10" style={{ background: "rgba(3,7,18,0.85)" }}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="h-8 w-8 rounded-md flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #D4AF37 0%, #F3CE5E 100%)" }}
            >
              <Sparkles size={14} className="text-[#030712]" />
            </div>
            <div>
              <div className="font-display font-black text-white text-sm leading-none">
                FAVOR <span className="text-[#D4AF37]">STUDIO</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/70 mt-1">
                Admin Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/55 hidden sm:inline" data-testid="admin-current-email">
              {me?.email}
            </span>
            <button
              onClick={loadAll}
              className="text-white/60 hover:text-white p-2"
              aria-label="Refresh"
              data-testid="admin-refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/70 hover:text-white"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-2">
            Always On Sales <span className="text-[#D4AF37]">control room.</span>
          </h1>
          <p className="text-white/55">Live registrations, captured leads, and chat activity.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard
            testid="stat-registrations"
            icon={<Users size={18} />}
            label="Registrations"
            value={stats.registrations}
            accent="#D4AF37"
          />
          <StatCard
            testid="stat-leads"
            icon={<Inbox size={18} />}
            label="Captured Leads"
            value={stats.leads}
            accent="#00F0FF"
          />
          <StatCard
            testid="stat-chats"
            icon={<MessageSquare size={18} />}
            label="Chat Sessions"
            value={stats.chat_sessions}
            accent="#0055FF"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 border-b border-white/8">
          <TabButton
            active={tab === "registrations"}
            onClick={() => setTab("registrations")}
            testid="tab-registrations"
          >
            Registrations · {regs.length}
          </TabButton>
          <TabButton
            active={tab === "leads"}
            onClick={() => setTab("leads")}
            testid="tab-leads"
          >
            Leads · {leads.length}
          </TabButton>
        </div>

        {error && (
          <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2 mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-white/55 py-12 text-center">Loading…</div>
        ) : tab === "registrations" ? (
          <RegistrationsTable rows={regs} />
        ) : (
          <LeadsTable rows={leads} />
        )}
      </div>
    </main>
  );
}

function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl p-5"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="inline-flex items-center justify-center h-9 w-9 rounded-md mb-4"
        style={{ background: `${accent}1f`, color: accent, border: `1px solid ${accent}40` }}
      >
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 mb-1">{label}</div>
      <div className="font-display font-black text-3xl text-white">{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold border-b-2 transition ${
        active
          ? "text-white border-[#D4AF37]"
          : "text-white/45 border-transparent hover:text-white/80"
      }`}
    >
      {children}
    </button>
  );
}

function RegistrationsTable({ rows }) {
  if (!rows.length) {
    return <EmptyState label="No registrations yet." />;
  }
  return (
    <div className="rounded-xl overflow-hidden border border-white/8" data-testid="registrations-table">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-white/55">
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Business</Th>
            <Th>Date</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`registration-row-${r.id}`}
              className="border-t border-white/5 hover:bg-white/[0.02]"
            >
              <Td className="font-medium">{r.name}</Td>
              <Td>
                <a href={`mailto:${r.email}`} className="text-cyan-300 hover:underline inline-flex items-center gap-1.5">
                  <Mail size={12} /> {r.email}
                </a>
              </Td>
              <Td>
                <span className="inline-flex items-center gap-1.5 text-white/75">
                  <Phone size={12} /> {r.phone}
                </span>
              </Td>
              <Td>
                <span className="inline-flex items-center gap-1.5 text-white/75">
                  <Briefcase size={12} /> {r.business}
                </span>
              </Td>
              <Td className="text-white/55 whitespace-nowrap">{fmtDate(r.created_at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadsTable({ rows }) {
  if (!rows.length) {
    return <EmptyState label="No chat-captured leads yet." />;
  }
  return (
    <div className="rounded-xl overflow-hidden border border-white/8" data-testid="leads-table">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-white/55">
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Question</Th>
            <Th>Source</Th>
            <Th>Date</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`lead-row-${r.id}`}
              className="border-t border-white/5 hover:bg-white/[0.02] align-top"
            >
              <Td className="font-medium">{r.name}</Td>
              <Td>
                <a href={`mailto:${r.email}`} className="text-cyan-300 hover:underline">
                  {r.email}
                </a>
              </Td>
              <Td className="max-w-md text-white/80 whitespace-pre-wrap">{r.question}</Td>
              <Td>
                <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-white/5 border border-white/10">
                  {r.source}
                </span>
              </Td>
              <Td className="text-white/55 whitespace-nowrap">{fmtDate(r.created_at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left text-[10px] font-bold uppercase tracking-[0.2em] px-5 py-3">
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 ${className}`}>{children}</td>;
}
function EmptyState({ label }) {
  return (
    <div
      className="rounded-xl border border-white/10 border-dashed py-16 text-center text-white/45"
      data-testid="empty-state"
    >
      {label}
    </div>
  );
}
